import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';

import Colours from '../definitions/Colours';
import Sizes from '../definitions/Sizes';
import Typography from '../definitions/Typography';
import DefaultIntlFormatValues from '../definitions/DefaultIntlFormatValues';
import toDate from '../functions/toDate';
import Paragraph from '../components/Paragraph';
import StarRating from '../components/StarRating';
import PageBehaviourButton from '../components/PageBehaviourButton';
import IconPlus from '../components/icons/IconPlus';
import IconMinus from '../components/icons/IconMinus';


function UserReviews({ title, overallRatingPercent, reviews, className }) {
    const reviewCount = reviews.length;
    const showMoreOrLessButton = reviewCount > FIRST_FEW_COMMENTS_COUNT;
    const [ moreToShow, setMoreToShow ] = useState(showMoreOrLessButton);

    if (showMoreOrLessButton) {
        reviews = reviews.slice(0, moreToShow ? FIRST_FEW_COMMENTS_COUNT : reviewCount);
    }

    return (
        <div className={className}>
            <If condition={title}>
                <h3 className="rh-title-m">
                    {title}
                </h3>
            </If>

            <div className="star-wrapper">
                <StarRating
                    ratingPercent={overallRatingPercent}
                    starSize={Sizes.ICONS['XS']}
                    className="star-rating"
                />

                <Paragraph
                    as="h4"
                    size="large"
                    className="user-reviews-rating"
                >
                    <FormattedMessage
                        id="base-ui.user-reviews-rating"
                        defaultMessage="<b>{ratingOutOf5, number, decimal1}</b>&nbsp;&nbsp;|&nbsp;&nbsp;<b>{reviewCount, number}</b> {reviewCount, plural, one {review} other {reviews}}"
                        values={{
                            ratingOutOf5: overallRatingPercent / 20,
                            reviewCount: reviewCount,
                        }}
                    />
                </Paragraph>
            </div>

            <hr className="user-reviews-horizontal-rule" />

            <ul className="user-review-list">
                <For
                    each="review"
                    of={reviews}
                    index="index"
                >
                    <li key={index}>
                        <p className="reviewer-name">
                            <FormattedMessage
                                id="base-ui.user-reviews-name-and-date"
                                defaultMessage={`<b>{name}</b> {commentDate, select,
    null {}
    other {<longDate>{commentDate, date, long}</longDate>}
}`}
                                values={{
                                    name: review.name,
                                    commentDate: toDate(review.date),
                                    ...DefaultIntlFormatValues,
                                }}
                            />
                        </p>

                        <StarRating
                            ratingPercent={review.rating * 20}
                            starSize="1.25rem"
                        />

                        <p className="review-comments">
                            {review.comments.replace(/\bi\b/g, 'I').replace(/'/g, '’')}
                        </p>
                    </li>
                </For>
            </ul>

            <If condition={showMoreOrLessButton}>
                <PageBehaviourButton
                    className="more-or-less"
                    onClick={() => setMoreToShow(!moreToShow)}
                    iconComponent={moreToShow ? IconPlus : IconMinus}
                    iconLocation="left"
                    size="small"
                >
                    <FormattedMessage
                        id="base-ui.user-reviews.show-more-or-less"
                        defaultMessage="{moreToShow, select, true {show more} other {show less}}"
                        values={{ moreToShow: moreToShow }}
                    />
                </PageBehaviourButton>
            </If>
        </div>
    );
}


UserReviews.propTypes = {
    title: PropTypes.node,

    overallRatingPercent: PropTypes.number.isRequired,

    reviews: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string.isRequired,
        rating: PropTypes.number.isRequired,
        comments: PropTypes.string.isRequired,
        date: PropTypes.oneOfType([
            PropTypes.object,   // date
            PropTypes.number,
            PropTypes.string,
        ]),
    })).isRequired,
    className: PropTypes.string,
};

UserReviews.defaultProps = {
    title: undefined,
    className: undefined,
};


const FIRST_FEW_COMMENTS_COUNT = 6;

const StyledUserReviews = styled(observer(UserReviews))`
    > .star-wrapper {
        display: flex;

        .star-rating {
            margin: ${Sizes.SPACING.QUARTER} ${Sizes.SPACING.THREE_QUARTERS} 0 0;
        }

        .user-reviews-rating {
            margin: 0;

            b {
                font-weight: ${Typography.WEIGHTS.MEDIUM};
            }
        }
    }

    > .user-reviews-horizontal-rule {
        margin: ${Sizes.SPACING.TWO} -4px;
        width: calc(100% + 8px);
        border: none;
        border-top: 1px solid ${Colours.STONE};
    }

    > ul.user-review-list {
        list-style-type: none;
        padding-left: 0;

        li {
            margin-bottom: ${Sizes.SPACING.THREE};

            p {
                margin: 0;

                &.reviewer-name {
                    margin-bottom: ${Sizes.SPACING.QUARTER};

                    b {
                        font-weight: ${Typography.WEIGHTS.MEDIUM};
                        margin-right: ${Sizes.SPACING.ONE};
                    }

                    time {
                        font-size: ${Sizes.FONTS.XS};
                        white-space: nowrap;
                    }
                }

                &.review-comments {
                    margin-top: ${Sizes.SPACING.THREE_QUARTERS};
                }
            }
        }
    }

    > .more-or-less {
        margin-top: ${Sizes.SPACING.THREE};
    }
`;


export default StyledUserReviews;
